import { useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import {
  useGetAgentWhatsappConversationQuery,
  useGetAgentWhatsappConversationsQuery,
  useSendAgentWhatsappMessageMutation,
} from "../../redux/api/agentWhatsappApi";
import { getTimeAgoString } from "../../constants/dateTime";
import { formatWhatsappPhone } from "./data";
import MessageThread from "./MessageThread";

const ConversationList = ({ conversations, activeId, onSelect, isLoading }) => (
  <Box sx={{ width: 300, flexShrink: 0, borderRight: "1px solid", borderColor: "divider" }}>
    <Typography variant="subtitle2" sx={{ px: 2, py: 1.5, fontWeight: 600 }}>
      Conversations
    </Typography>
    <Divider />
    {isLoading ? (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    ) : conversations.length ? (
      <List disablePadding>
        {conversations.map((conversation) => (
          <ListItemButton
            key={conversation.id}
            selected={conversation.id === activeId}
            onClick={() => onSelect(conversation.id)}
          >
            <ListItemText
              primary={formatWhatsappPhone(conversation.phoneNumber)}
              secondary={conversation.lastMessageAt ? getTimeAgoString(conversation.lastMessageAt) : ""}
              primaryTypographyProps={{ fontWeight: 600 }}
            />
            {conversation.status && (
              <Chip label={conversation.status} size="small" variant="outlined" />
            )}
          </ListItemButton>
        ))}
      </List>
    ) : (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        No conversations yet.
      </Typography>
    )}
  </Box>
);

const AgentWhatsappInbox = () => {
  const { data: conversations = [], isLoading } = useGetAgentWhatsappConversationsQuery();
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    if (!activeId && conversations.length) setActiveId(conversations[0].id);
  }, [conversations, activeId]);

  const { data, isLoading: isThreadLoading } = useGetAgentWhatsappConversationQuery(
    { conversationId: activeId },
    { skip: !activeId }
  );
  const [sendMessage, { isLoading: isSending }] = useSendAgentWhatsappMessageMutation();

  const activeConversation =
    data?.conversation || conversations.find((item) => item.id === activeId);

  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: 3, display: "flex", height: "calc(100vh - 340px)", minHeight: 380, overflow: "hidden" }}
    >
      <ConversationList
        conversations={conversations}
        activeId={activeId}
        onSelect={setActiveId}
        isLoading={isLoading}
      />
      <MessageThread
        title={activeConversation ? formatWhatsappPhone(activeConversation.phoneNumber) : ""}
        subtitle={activeConversation?.leadId ? `Lead #${activeConversation.leadId}` : ""}
        messages={data?.messages || []}
        isLoading={!!activeId && isThreadLoading}
        isSending={isSending}
        onSend={(message) =>
          sendMessage({ conversationId: activeId, message }).unwrap()
        }
      />
    </Paper>
  );
};

export default AgentWhatsappInbox;
